import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { historyApi } from '../api/historyContributions';

/**
 * Renders a private contribution file. The URL needs the session cookie, and
 * a plain <img src> would work for images but not give us a graceful
 * fallback for PDFs/documents — so fetch the bytes once and show whatever fits.
 */
export function ProtectedFile({
  url,
  contentType,
  filename,
  className = 'h-32 w-32 rounded object-cover',
}: {
  url: string;
  contentType: string;
  filename: string;
  className?: string;
}) {
  const isImage = contentType.startsWith('image/');
  const q = useQuery({
    queryKey: ['contribution-file', url],
    queryFn: () => historyApi.fileBlob(url),
    staleTime: 5 * 60_000,
    enabled: isImage,
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);

  if (isImage) {
    return objectUrl ? (
      <img src={objectUrl} alt={filename} className={className} />
    ) : (
      <div className={`${className} animate-pulse bg-ink-800/60`} aria-label={`Loading ${filename}`} />
    );
  }
  return (
    <button
      type="button"
      className={`${className} flex flex-col items-center justify-center gap-1 border border-ink-800 bg-ink-900/40 text-center text-xs text-cream-300/70 hover:border-gold-600/40`}
      onClick={async () => {
        const blob = await historyApi.fileBlob(url);
        const u = URL.createObjectURL(blob);
        window.open(u, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(u), 60_000);
      }}
      title={`Open ${filename}`}
    >
      <span className="text-2xl">📄</span>
      <span className="line-clamp-2 break-all px-1">{filename}</span>
    </button>
  );
}
